/**
 * Shared reading of a program offer, so the dashboard panel and the popup describe the same offer
 * the same way.
 */
import { formatDate } from "../../utils/portalHelpers";

const CURRENCY_FORMATS = {
  AUD: { locale: "en-AU", currency: "AUD" },
  NPR: { locale: "ne-NP", currency: "NPR" },
  USD: { locale: "en-US", currency: "USD" },
};

/** The offer's own currency, not the portal's: an offer may be run in NPR for Nepal partners. */
export const offerReward = (offer) => {
  const amount = Number(offer?.rewardAmount);
  if (!Number.isFinite(amount) || amount === 0) return null;
  const format = CURRENCY_FORMATS[offer?.rewardCurrency] || CURRENCY_FORMATS.AUD;
  return new Intl.NumberFormat(format.locale, {
    style: "currency",
    currency: format.currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

/** "3 admissions", or just the count when the office did not name what is being counted. */
export const offerTarget = (offer) => {
  const count = Number(offer?.targetCount);
  if (!Number.isFinite(count) || count <= 0) return null;
  const label = (offer?.targetLabel || "").trim();
  return label ? `${count} ${label}` : String(count);
};

export const offerDeadline = (offer) => (offer?.deadline ? formatDate(offer.deadline) : null);

export const offerDetailPath = (offer) => `/partner/offers/${offer.id}`;

/**
 * Which popup offers this browser has already shown. An offer pops up once per browser, not once per
 * sign-in: it is marked the moment it appears, so signing out and back in does not bring it back.
 * Kept in the browser rather than on the server because it is a per-device convenience, and it must
 * degrade to "show it" rather than throw when site data is blocked.
 */
const SEEN_KEY = "partner.offerPopupsDismissed";

const readSeen = () => {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (e) {
    return [];
  }
};

export const markOfferPopupSeen = (offerId) => {
  try {
    const next = Array.from(new Set([...readSeen(), String(offerId)]));
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(next));
  } catch (e) {
    // A viewer with site data blocked simply sees the offer again next time, which is harmless.
  }
};

/** The first offer the office asked to surface on entry that this browser has not shown yet. */
export const nextPopupOffer = (offers) => {
  const seen = readSeen();
  return (
    (offers || []).find((offer) => offer?.showOnLoginPopup && !seen.includes(String(offer.id))) ||
    null
  );
};
